// ============================================================================
// Caja de contingencia (modo contingencia, paso 3c · oct-2026)
//
// Una pantalla aparte para trabajar sin internet en el PC de caja. Las
// pantallas de siempre (mesas, POS, cobro) NO se tocan: esta se abre encima,
// en color ámbar, desde la franja roja o desde el indicador de conexión.
//
// Trabaja con la copia local (carta, mesas y pedidos abiertos, paso 3b) y
// guarda cada cambio en el PC (IndexedDB). En pantalla solo aparece lo que el
// almacén local confirmó. Al volver la conexión, SubidaContingencia lo sube al
// servidor con su llave y su hora original (paso 3d): lo cobrado queda
// facturado y pagado, lo abierto pasa a la pantalla de siempre, y lo que el
// servidor rechace queda aquí en "Requiere revisión". La lógica está en
// src/lib/caja-contingencia.ts y src/lib/subida-contingencia.ts.
// ============================================================================
import { useEffect, useMemo, useState } from 'react';
import { X, Printer, Minus, Plus, Search, AlertTriangle, ShoppingBag, CheckCircle2, RefreshCw } from 'lucide-react';
import { useCaja, cerrarCaja, guardarPedido, actualizarPedido, reintentarSubida } from '../../contingencia/caja';
import { useUser } from '../../context/user-context';
import { useBranch } from '../../context/branch-context';
import { useTextos } from '../../context/regional-context';
import { useConexion } from '../../hooks/use-conexion';
import { textosContingencia } from '../../../i18n/modulos/contingencia';
import type { VarsTraduccion } from '../../../i18n/traducir';
import { leerCopia } from '../../../lib/almacen-local';
import { claveCopia, frescuraCopia, type CopiaLocal } from '../../../lib/copia-local';
import { nuevaLlave } from '../../../lib/conexion';
import { formatMoney } from '@/lib/moneda';
import * as caja from '../../../lib/caja-contingencia';
import { nombreDestino, type PedidoContingencia, type MetodoContingencia } from '../../../lib/caja-contingencia';
import type { ApiProducto } from '../../../api/menu';
import type { Mesa } from '../../../api/mesas';
import type { PedidoConItems } from '../../../api/pedidos';
import { CobroContingencia } from './cobro-contingencia';
import { imprimirResumenContingencia } from './resumen-contingencia';

type Copia = CopiaLocal<ApiProducto, Mesa, PedidoConItems>;
type Clave = keyof typeof textosContingencia.es;
export type TraductorCaja = (clave: Clave, vars?: VarsTraduccion) => string;

const ahoraIso = () => new Date().toISOString();
const horaCorta = (ms: number) => new Date(ms).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

/** Siempre montado: carga los pedidos locales al arrancar (para el indicador) y pinta la caja si está abierta. */
export function CajaContingencia() {
  const { abierta, pedidos, subiendo } = useCaja();
  if (!abierta) return null;
  return <CajaAbierta pedidos={pedidos} subiendo={subiendo} />;
}

/** Lo subido hace rato ya no hace falta verlo en la lista (sigue guardado en el PC). */
const LISTA_SUBIDOS_MS = 12 * 60 * 60 * 1000;

function CajaAbierta({ pedidos, subiendo }: { pedidos: PedidoContingencia[]; subiendo: boolean }) {
  const t = useTextos(textosContingencia) as TraductorCaja;
  const { user } = useUser();
  const { currentBranchId, currentBranch } = useBranch();
  const { estado } = useConexion();
  const hayConexion = estado !== 'sin_conexion' && estado !== 'comprobando';
  const [copia, setCopia] = useState<Copia | null | undefined>(undefined);
  const [seleccion, setSeleccion] = useState<string | null>(null);
  const [borrador, setBorrador] = useState<PedidoContingencia | null>(null);
  const [buscar, setBuscar] = useState('');
  const [grupo, setGrupo] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cobrando, setCobrando] = useState(false);

  useEffect(() => {
    if (!currentBranchId) { setCopia(null); return; }
    let vivo = true;
    leerCopia<Copia>(claveCopia(currentBranchId))
      .then(c => { if (vivo) setCopia(c ?? null); })
      .catch(() => { if (vivo) setCopia(null); });
    return () => { vivo = false; };
  }, [currentBranchId]);

  const delaSede = useMemo(
    () => pedidos.filter(p => p.sedeId === currentBranchId).sort((a, b) => b.creadoEn.localeCompare(a.creadoEn)),
    [pedidos, currentBranchId],
  );
  const actual = borrador ?? delaSede.find(p => p.id === seleccion) ?? null;
  const pendientes = caja.pendientesDeSubir(delaSede);
  const enRevision = delaSede.filter(p => p.estado === 'requiere_revision').length;
  const enLista = delaSede.filter(p => p.estado !== 'subido' || p.id === actual?.id
    || Date.now() - Date.parse(p.subida?.terminadoEn ?? p.creadoEn) < LISTA_SUBIDOS_MS);

  /** Un pedido que se subió abierto y la copia todavía no trae (la red se volvió a caer enseguida). */
  const subidoAbiertoDe = (m: Mesa) => {
    if (!copia) return null;
    // delaSede va del más nuevo al más viejo: gana el último que se subió.
    for (const p of delaSede) {
      if (p.destino.tipo !== 'mesa' || p.destino.mesaId !== m.id) continue;
      const ref = caja.referenciaDeSubido(p);
      if (ref && Date.parse(p.subida?.terminadoEn ?? '') > copia.tomadaEn) return ref;
    }
    return null;
  };

  const platos = useMemo(() => (copia?.menu ?? []).filter(p => p.activo !== false), [copia]);
  const grupos = useMemo(
    () => Array.from(new Set(platos.map(p => p.subcategoria || p.categoria))).sort((a, b) => a.localeCompare(b)),
    [platos],
  );
  const visibles = useMemo(() => {
    const q = buscar.trim().toLowerCase();
    return platos.filter(p =>
      (!grupo || (p.subcategoria || p.categoria) === grupo) &&
      (!q || p.nombre.toLowerCase().includes(q)));
  }, [platos, grupo, buscar]);

  // ---- acciones (la pantalla cambia SOLO si el PC confirmó el guardado) ----
  // Un pedido nuevo se guarda entero. Uno que ya está guardado se cambia sobre
  // su última versión: la subida puede haberlo tocado mientras tanto.
  const aplicar = async (cambiar: (p: PedidoContingencia) => PedidoContingencia) => {
    if (!actual) return;
    setOcupado(true);
    setError(null);
    try {
      if (borrador && actual.id === borrador.id) {
        const p = cambiar(borrador);
        await guardarPedido(p);
        setBorrador(null);
        setSeleccion(p.id);
      } else {
        await actualizarPedido(actual.id, cambiar);
      }
    } catch (e) {
      if (e instanceof caja.ErrorCaja) {
        setError(e.message);
      } else {
        console.error('[contingencia] no se pudo guardar el pedido local:', e);
        setError(t('noSeGuardo'));
      }
    } finally {
      setOcupado(false);
    }
  };

  const reintentar = async (id: string) => {
    setError(null);
    try {
      await reintentarSubida(id);
    } catch (e) {
      console.error('[contingencia] no se pudo reintentar la subida:', e);
      setError(t('noSeGuardo'));
    }
  };

  const abrirMesa = (m: Mesa) => {
    if (!user || !currentBranchId || !copia) return;
    setError(null);
    const enCurso = delaSede.find(p => p.destino.tipo === 'mesa' && p.destino.mesaId === m.id && p.estado === 'abierto');
    if (enCurso) { setBorrador(null); setSeleccion(enCurso.id); return; }
    const delServidor = copia.pedidos.find(pd =>
      pd.mesa_id === m.id || (m.grupo_id != null && pd.grupo_id === m.grupo_id));
    // Se subió abierto desde aquí después de la última copia (la red se volvió
    // a caer enseguida): eso es lo que hay en el servidor, no lo de la copia.
    const subidoAbierto = subidoAbiertoDe(m);
    const idServidor = subidoAbierto?.pedidoId ?? delServidor?.id ?? null;
    // Ya se cobró acá sin conexión: se muestra ese, no se abre otro cobro.
    const yaCobrado = idServidor != null && delaSede.find(p => p.servidor?.pedidoId === idServidor && !!p.cobro);
    if (yaCobrado) { setBorrador(null); setSeleccion(yaCobrado.id); return; }
    setSeleccion(null);
    setBorrador(caja.nuevoPedido({
      id: nuevaLlave(), sedeId: currentBranchId, usuarioId: Number(user.id),
      destino: { tipo: 'mesa', mesaId: m.id, mesaNombre: m.nombre, grupoId: m.grupo_id ?? null },
      servidor: subidoAbierto ?? (delServidor ? {
        pedidoId: delServidor.id,
        referencia: delServidor.referencia,
        lineas: (delServidor.items || []).map(it => ({
          id: it.id, nombre: it.nombre, cantidad: Number(it.cantidad) || 0, subtotal: Number(it.subtotal) || 0,
        })),
        total: Number(delServidor.total) || 0,
        facturado: !!delServidor.venta_id || delServidor.estado === 'facturado',
      } : null),
      ahora: ahoraIso(), llave: nuevaLlave(),
    }));
  };

  const nuevoParaLlevar = () => {
    if (!user || !currentBranchId) return;
    setError(null);
    const n = delaSede.filter(p => p.destino.tipo === 'para_llevar').length + 1;
    setSeleccion(null);
    setBorrador(caja.nuevoPedido({
      id: nuevaLlave(), sedeId: currentBranchId, usuarioId: Number(user.id),
      destino: { tipo: 'para_llevar', etiqueta: `${t('paraLlevar')} ${n}` },
      servidor: null, ahora: ahoraIso(), llave: nuevaLlave(),
    }));
  };

  const tocarPlato = (prod: ApiProducto) => {
    if (!actual) { setError(t('eligeAntes')); return; }
    if (ocupado) return;
    void aplicar(p => caja.agregarPlato(p, prod, { ahora: ahoraIso(), id: nuevaLlave(), llave: nuevaLlave() }));
  };

  const quitar = (lineaId: string) => {
    if (!actual || ocupado) return;
    void aplicar(p => caja.quitarUno(p, lineaId));
  };

  const confirmarCobro = async (datos: { propina: number; pagos: caja.PagoLocal[]; correoFE?: string }) => {
    if (!actual) return false;
    const problema = caja.problemaDeCobro(actual, datos);
    if (problema) { setError(problema); return false; }
    await aplicar(p => caja.cobrar(p, datos, { ahora: ahoraIso(), llaveFacturar: nuevaLlave(), llavePagar: nuevaLlave() }));
    return true;
  };

  // ---- estado de cada mesa según la copia y lo hecho acá ----
  const estadoMesa = (m: Mesa): 'libre' | 'ocupada' | 'local' => {
    if (delaSede.some(p => p.destino.tipo === 'mesa' && p.destino.mesaId === m.id && p.estado === 'abierto' && p.lineas.length > 0)) return 'local';
    if (copia?.pedidos.some(pd => pd.mesa_id === m.id || (m.grupo_id != null && pd.grupo_id === m.grupo_id))) return 'ocupada';
    if (subidoAbiertoDe(m)) return 'ocupada';
    return 'libre';
  };

  const frescura = frescuraCopia(copia?.tomadaEn, Date.now());

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('titulo')}
      data-testid="caja-contingencia"
      className="fixed inset-0 z-[60] flex flex-col bg-[var(--surface-2)] text-[var(--ink)]"
    >
      {/* ---- Barra ámbar: la app se ve distinta mientras dura la contingencia ---- */}
      <header className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-1 bg-[var(--warn)] px-4 py-3">
        <div className="text-[15px] font-extrabold uppercase tracking-wide">
          {t('titulo')} · {estado === 'sin_conexion' ? t('sinConexion') : t('conConexion')}
        </div>
        <div className="text-[13px] font-semibold opacity-80">{currentBranch}</div>
        {copia && <div className="text-[13px] font-semibold opacity-80">{t('datosDe', { hora: horaCorta(copia.tomadaEn) })}</div>}
        <div className="flex-1" />
        <div data-testid="caja-pendientes" className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-[12.5px] font-extrabold text-[var(--warn-ink)]">
          {subiendo && <RefreshCw className="size-3.5 animate-spin" aria-hidden="true" />}
          {subiendo ? t('subiendo') : pendientes === 1 ? t('pendientes1') : t('pendientesN', { n: pendientes })}
        </div>
        <button
          type="button"
          onClick={cerrarCaja}
          className="press inline-flex cursor-pointer items-center gap-1.5 rounded-lg border-0 bg-white/60 px-3 py-1.5 text-[13px] font-bold text-[var(--ink)]"
        >
          <X className="size-4" /> {t('volver')}
        </button>
      </header>

      {frescura === 'vieja' && (
        <div className="flex shrink-0 items-center gap-2 bg-[var(--warn-bg)] px-4 py-2 text-[13px] font-semibold text-[var(--warn-ink)]">
          <AlertTriangle className="size-4 shrink-0" /> {t('copiaVieja')}
        </div>
      )}
      {hayConexion && pendientes > enRevision && (
        <div data-testid="caja-volvio-conexion" className="flex shrink-0 items-center gap-2 bg-[var(--warn-bg)] px-4 py-2 text-[13px] font-semibold text-[var(--warn-ink)]">
          <RefreshCw className="size-4 shrink-0" /> {t('volvioConexion')}
        </div>
      )}
      {hayConexion && pendientes === 0 && delaSede.some(p => p.estado === 'subido') && (
        <div data-testid="caja-todo-subido" className="flex shrink-0 items-center gap-2 bg-[var(--ok-bg)] px-4 py-2 text-[13px] font-semibold text-[var(--ok-ink)]">
          <CheckCircle2 className="size-4 shrink-0" /> {t('todoSubido')}
        </div>
      )}
      {enRevision > 0 && (
        <div data-testid="caja-hay-revision" className="flex shrink-0 items-center gap-2 bg-[var(--danger-bg)] px-4 py-2 text-[13px] font-bold text-[var(--danger-ink)]">
          <AlertTriangle className="size-4 shrink-0" /> {enRevision === 1 ? t('hayRevision1') : t('hayRevisionN', { n: enRevision })}
        </div>
      )}
      {error && (
        <div role="alert" className="flex shrink-0 items-center gap-2 bg-[var(--danger-bg)] px-4 py-2 text-[13px] font-bold text-[var(--danger-ink)]">
          <AlertTriangle className="size-4 shrink-0" /> {error}
        </div>
      )}

      {copia === undefined ? (
        <div className="m-auto text-[14px] font-semibold text-[var(--muted-ink)]">{t('cargando')}</div>
      ) : copia === null ? (
        <div className="m-auto max-w-md px-6 text-center text-[15px] font-semibold text-[var(--muted-ink)]">{t('sinCopia')}</div>
      ) : (
        <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 overflow-y-auto p-3 lg:grid-cols-[260px_1fr_340px] lg:overflow-hidden">
          {/* ---- Mesas, para llevar y lo hecho en este equipo ---- */}
          <aside className="flex min-h-0 flex-col gap-3 lg:overflow-y-auto">
            <button
              type="button"
              onClick={nuevoParaLlevar}
              data-testid="caja-nuevo-para-llevar"
              className="press inline-flex cursor-pointer items-center justify-center gap-2 rounded-xl border-0 bg-[var(--ink)] px-3 py-2.5 text-[13.5px] font-bold text-white"
            >
              <ShoppingBag className="size-4" /> {t('nuevoParaLlevar')}
            </button>
            <section>
              <h3 className="m-0 mb-2 text-[12px] font-extrabold uppercase tracking-wide text-[var(--muted-ink)]">{t('mesas')}</h3>
              <div className="grid grid-cols-3 gap-2">
                {copia.mesas.filter(m => m.activa !== false).map(m => {
                  const e = estadoMesa(m);
                  const elegida = actual?.destino.tipo === 'mesa' && actual.destino.mesaId === m.id;
                  return (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => abrirMesa(m)}
                      data-testid="caja-mesa"
                      data-mesa-id={m.id}
                      data-estado={e}
                      title={e === 'local' ? t('enContingencia') : e === 'ocupada' ? t('ocupada') : t('libre')}
                      className={`press cursor-pointer rounded-xl border-2 px-2 py-2 text-[12.5px] font-bold ${
                        elegida ? 'border-[var(--ink)]' : 'border-transparent'
                      } ${
                        e === 'local' ? 'bg-[var(--warn-bg)] text-[var(--warn-ink)]'
                          : e === 'ocupada' ? 'bg-[var(--danger-bg)] text-[var(--danger-ink)]'
                          : 'bg-[var(--surface)] text-[var(--ink)]'
                      }`}
                    >
                      {m.nombre}
                    </button>
                  );
                })}
              </div>
            </section>
            <section>
              <h3 className="m-0 mb-2 text-[12px] font-extrabold uppercase tracking-wide text-[var(--muted-ink)]">{t('enEsteEquipo')}</h3>
              {enLista.length === 0 ? (
                <p className="m-0 text-[13px] text-[var(--muted-ink)]">{t('ninguno')}</p>
              ) : (
                <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
                  {enLista.map(p => (
                    <li key={p.id}>
                      <button
                        type="button"
                        onClick={() => { setBorrador(null); setSeleccion(p.id); setError(null); }}
                        data-testid="caja-pedido-local"
                        data-estado={p.estado}
                        className={`press flex w-full cursor-pointer items-center justify-between gap-2 rounded-lg border px-2.5 py-2 text-left text-[12.5px] ${
                          actual?.id === p.id ? 'border-[var(--ink)] bg-[var(--surface)]' : 'border-[var(--line)] bg-[var(--surface)]'
                        }`}
                      >
                        <span className="font-bold">{nombreDestino(p)}</span>
                        <span className={p.estado === 'requiere_revision' ? 'font-bold text-[var(--danger-ink)]' : 'text-[var(--muted-ink)]'}>
                          {subiendo && caja.necesitaSubida(p) ? t('subiendo') : etiquetaEstado(t, p)}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </aside>

          {/* ---- Carta de la copia local ---- */}
          <main className="flex min-h-0 flex-col gap-2 lg:overflow-hidden">
            <label className="flex items-center gap-2 rounded-xl border border-[var(--line-2)] bg-[var(--surface)] px-3 py-2">
              <Search className="size-4 text-[var(--muted-ink)]" />
              <input
                value={buscar}
                onChange={e => setBuscar(e.target.value)}
                placeholder={t('buscarPlato')}
                className="w-full border-0 bg-transparent text-[14px] outline-none"
              />
            </label>
            <div className="flex flex-wrap gap-1.5">
              {[null, ...grupos].map(g => (
                <button
                  key={g ?? '__todas'}
                  type="button"
                  onClick={() => setGrupo(g)}
                  className={`press cursor-pointer rounded-full border px-3 py-1 text-[12.5px] font-bold ${
                    grupo === g ? 'border-[var(--ink)] bg-[var(--ink)] text-white' : 'border-[var(--line-2)] bg-[var(--surface)] text-[var(--ink)]'
                  }`}
                >
                  {g ?? t('todas')}
                </button>
              ))}
            </div>
            <div className="grid min-h-0 grid-cols-2 content-start gap-2 overflow-y-auto sm:grid-cols-3 xl:grid-cols-4">
              {visibles.map(p => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => tocarPlato(p)}
                  disabled={ocupado || !actual || !caja.editable(actual)}
                  data-testid="caja-plato"
                  className="press flex cursor-pointer flex-col items-start gap-1 rounded-xl border border-[var(--line)] bg-[var(--surface)] p-3 text-left disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <span className="text-[13.5px] font-bold leading-tight">{p.nombre}</span>
                  <span className="text-[13px] font-extrabold text-[var(--ok-ink)]">{formatMoney(Number(p.precio))}</span>
                  {p.tiene_modificadores && <span className="text-[11px] font-semibold text-[var(--warn-ink)]">{t('opcionesEnPapel')}</span>}
                </button>
              ))}
            </div>
          </main>

          {/* ---- Ticket ---- */}
          <Ticket
            t={t}
            pedido={actual}
            ocupado={ocupado}
            subiendo={subiendo}
            hayConexion={hayConexion}
            onReintentar={reintentar}
            guardado={!!actual && !borrador}
            onQuitar={quitar}
            onImprimir={() => {
              if (!actual) return;
              if (!imprimirResumenContingencia(actual, currentBranch, t)) setError(t('habilitaPopups'));
            }}
            onCobrar={() => setCobrando(true)}
          />
        </div>
      )}

      {cobrando && actual && (
        <CobroContingencia
          t={t}
          pedido={actual}
          onCancelar={() => setCobrando(false)}
          onConfirmar={async (datos) => { if (await confirmarCobro(datos)) setCobrando(false); }}
        />
      )}
    </div>
  );
}

export function etiquetaEstado(t: TraductorCaja, p: PedidoContingencia): string {
  switch (p.estado) {
    case 'cobrado': return t('cobradoPendiente');
    case 'subiendo': return t('subiendo');
    case 'subido':
      if (!p.cobro) return t('enSistema');
      return p.subida?.factura ? t('subidoFactura', { factura: p.subida.factura }) : t('subido');
    case 'requiere_revision': return t('requiereRevision');
    default: return formatMoney(caja.consumo(p));
  }
}

const PASO: Record<NonNullable<PedidoContingencia['errorEn']>, Clave> = {
  crear: 'pasoCrear', agregar: 'pasoAgregar', facturar: 'pasoFacturar', pagar: 'pasoPagar',
};

function Ticket({ t, pedido, ocupado, subiendo, hayConexion, onReintentar, guardado, onQuitar, onImprimir, onCobrar }: {
  t: TraductorCaja;
  pedido: PedidoContingencia | null;
  ocupado: boolean;
  subiendo: boolean;
  hayConexion: boolean;
  onReintentar: (id: string) => void;
  guardado: boolean;
  onQuitar: (lineaId: string) => void;
  onImprimir: () => void;
  onCobrar: () => void;
}) {
  if (!pedido) {
    return (
      <section className="flex items-center justify-center rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-6 text-center text-[14px] font-semibold text-[var(--muted-ink)]">
        {t('eligeDestino')}
      </section>
    );
  }
  const total = caja.consumo(pedido);
  const sinNada = pedido.lineas.length === 0 && (pedido.servidor?.lineas.length ?? 0) === 0;
  const puedeCobrar = pedido.estado === 'abierto' && !pedido.cobro && !sinNada;
  return (
    <section data-testid="caja-ticket" className="flex min-h-0 flex-col rounded-2xl border border-[var(--line)] bg-[var(--surface)]">
      <div className="flex items-center justify-between gap-2 border-b border-[var(--line)] px-4 py-3">
        <div>
          <div className="text-[16px] font-extrabold">{nombreDestino(pedido)}</div>
          {pedido.servidor && <div className="text-[12px] text-[var(--muted-ink)]">{pedido.servidor.referencia}</div>}
        </div>
        <span data-testid="caja-ticket-estado" className="rounded-full bg-[var(--warn-bg)] px-2.5 py-1 text-[11.5px] font-bold text-[var(--warn-ink)]">
          {ocupado ? t('guardando')
            : subiendo && caja.necesitaSubida(pedido) ? t('subiendo')
            : pedido.estado === 'abierto' ? (guardado ? t('guardadoEquipo') : '')
            : etiquetaEstado(t, pedido)}
        </span>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-2">
        {pedido.estado === 'requiere_revision' && (
          <div data-testid="caja-revision" className="mb-2 rounded-xl bg-[var(--danger-bg)] p-3 text-[12.5px] text-[var(--danger-ink)]">
            <p className="m-0 font-bold">{t('revisionTitulo', { paso: t(PASO[pedido.errorEn ?? 'crear']) })}</p>
            <p className="m-0 mt-1">{pedido.error}</p>
            <p className="m-0 mt-1">{t('revisionQueHacer')}</p>
            <button
              type="button"
              onClick={() => onReintentar(pedido.id)}
              disabled={!hayConexion || subiendo}
              data-testid="caja-reintentar"
              className="press mt-2 inline-flex cursor-pointer items-center gap-1.5 rounded-lg border-0 bg-[var(--surface)] px-3 py-1.5 text-[12.5px] font-bold text-[var(--ink)] disabled:cursor-not-allowed disabled:opacity-50"
            >
              <RefreshCw className="size-3.5" /> {t('reintentarSubida')}
            </button>
          </div>
        )}
        {pedido.estado === 'subido' && (
          <p data-testid="caja-subido-nota" className="m-0 mb-2 rounded-xl bg-[var(--ok-bg)] p-3 text-[12.5px] font-semibold text-[var(--ok-ink)]">
            {pedido.cobro
              ? t('subidoNota', { factura: pedido.subida?.factura ?? '-' })
              : t('enSistemaNota', { referencia: pedido.subida?.referencia ?? pedido.servidor?.referencia ?? '' })}
          </p>
        )}
        {pedido.cobro?.correoFE && pedido.estado === 'subido' && (
          <p data-testid="caja-pidio-fe" className="m-0 mb-2 text-[12.5px] font-semibold text-[var(--warn-ink)]">{t('pidioFE', { correo: pedido.cobro.correoFE })}</p>
        )}
        {pedido.servidor?.facturado && <p className="m-0 mb-2 text-[12.5px] font-semibold text-[var(--warn-ink)]">{t('facturadoNota')}</p>}
        {pedido.servidor && pedido.servidor.lineas.length > 0 && caja.editable(pedido) && (
          <p className="m-0 mb-1 text-[11.5px] text-[var(--muted-ink)]">{t('yaEstabaNota')}</p>
        )}
        <ul className="m-0 list-none p-0">
          {(pedido.servidor?.lineas ?? []).map(l => (
            <li key={`s${l.id}`} className="flex items-center justify-between gap-2 border-b border-[var(--line)] py-2 text-[13px] text-[var(--muted-ink)]">
              <span>{l.cantidad} × {l.nombre} <em className="text-[11px]">({t('yaEstaba')})</em></span>
              <span className="tabular-nums">{formatMoney(l.subtotal)}</span>
            </li>
          ))}
          {pedido.lineas.map(l => (
            <li key={l.id} data-testid="caja-linea" className="flex items-center justify-between gap-2 border-b border-[var(--line)] py-2 text-[13.5px]">
              <span className="flex items-center gap-2">
                {caja.editable(pedido) && !l.enviada && (
                  <button
                    type="button"
                    aria-label="-1"
                    onClick={() => onQuitar(l.id)}
                    disabled={ocupado}
                    className="press inline-flex size-7 cursor-pointer items-center justify-center rounded-lg border border-[var(--line-2)] bg-[var(--surface-2)]"
                  >
                    {l.cantidad > 1 ? <Minus className="size-3.5" /> : <X className="size-3.5" />}
                  </button>
                )}
                <b>{l.cantidad}</b> × {l.nombre}
                {l.enviada && pedido.estado !== 'subido' && <em className="text-[11px] text-[var(--muted-ink)]">({t('enviado')})</em>}
              </span>
              <span className="tabular-nums font-semibold">{formatMoney(l.precio * l.cantidad)}</span>
            </li>
          ))}
        </ul>
        {sinNada && <p className="mt-6 text-center text-[13px] text-[var(--muted-ink)]"><Plus className="inline size-4" /> {t('sinPlatos')}</p>}
      </div>
      <div className="border-t border-[var(--line)] px-4 py-3">
        {pedido.cobro ? (
          <>
            <Fila etiqueta={t('consumo')} valor={formatMoney(total)} />
            <Fila etiqueta={t('propina')} valor={formatMoney(pedido.cobro.propina)} />
            <Fila etiqueta={t('total')} valor={formatMoney(pedido.cobro.pagos.reduce((s, p) => s + p.monto, 0))} fuerte />
          </>
        ) : (
          <Fila etiqueta={pedido.servidor?.facturado ? t('total') : t('consumo')} valor={formatMoney(pedido.servidor?.facturado ? pedido.servidor.total : total)} fuerte />
        )}
        <div className="mt-3 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={onImprimir}
            disabled={sinNada}
            data-testid="caja-imprimir"
            className="press inline-flex cursor-pointer items-center justify-center gap-1.5 rounded-xl border border-[var(--line-2)] bg-[var(--surface-2)] px-3 py-2.5 text-[13px] font-bold disabled:opacity-50"
          >
            <Printer className="size-4" /> {t('imprimirResumen')}
          </button>
          <button
            type="button"
            onClick={onCobrar}
            disabled={!puedeCobrar || ocupado}
            data-testid="caja-cobrar"
            className="press inline-flex cursor-pointer items-center justify-center rounded-xl border-0 bg-[var(--ok)] px-3 py-2.5 text-[13.5px] font-extrabold text-white disabled:opacity-50"
          >
            {t('cobrar')}
          </button>
        </div>
      </div>
    </section>
  );
}

function Fila({ etiqueta, valor, fuerte = false }: { etiqueta: string; valor: string; fuerte?: boolean }) {
  return (
    <div className={`flex justify-between py-0.5 ${fuerte ? 'text-[17px] font-extrabold' : 'text-[13px] text-[var(--muted-ink)]'}`}>
      <span>{etiqueta}</span>
      <span className="tabular-nums" data-testid={fuerte ? 'caja-total' : undefined}>{valor}</span>
    </div>
  );
}

export type { MetodoContingencia };
